import type {
  AirwallexPayment,
  MercuryPayment,
  PaypalPayment,
  QueryConstraint,
} from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useCallback, useState } from "react";
import { fetchAllPages } from "../utils/fetch-all-pages";

const databaseService = serviceHost.getDatabaseService();
const paypalPaymentRepository =
  repositoryHost.getPaypalPaymentRepository(databaseService);
const mercuryPaymentRepository =
  repositoryHost.getMercuryPaymentRepository(databaseService);
const airwallexPaymentRepository =
  repositoryHost.getAirwallexPaymentRepository(databaseService);

export interface InboxExportFilters {
  // "all" | one of the match statuses.
  statusFilter: string;
  // Bounds on paidAt (inclusive) for the selected date range.
  startDate?: Date;
  endDate?: Date;
}

export interface InboxExportRecords {
  paypal: PaypalPayment[];
  mercury: MercuryPayment[];
  airwallex: AirwallexPayment[];
}

function buildConstraints(filters: InboxExportFilters): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  if (filters.statusFilter !== "all") {
    constraints.push({
      field: "matchStatus",
      operator: "==",
      value: filters.statusFilter,
    });
  }
  if (filters.startDate) {
    constraints.push({
      field: "paidAt",
      operator: ">=",
      value: filters.startDate,
    });
  }
  if (filters.endDate) {
    constraints.push({
      field: "paidAt",
      operator: "<=",
      value: filters.endDate,
    });
  }
  return constraints;
}

/**
 * Fetches ALL inbox records matching the current status + date filters across
 * the three sources, for CSV export (source/direction filters are applied by
 * the caller after mapping). Separate from the paginated inbox queries so the
 * export isn't limited to the pages already loaded on screen.
 */
export function useInboxExport() {
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(
    async (filters: InboxExportFilters): Promise<InboxExportRecords> => {
      setExporting(true);
      try {
        // Auto-paged rather than one large `limit`, which silently truncates
        // once a source outgrows it (PayPal alone is already in the thousands).
        // The three inbox entities share these field names but not their types,
        // so each pager gets the constraints cast to its own entity.
        const constraints = buildConstraints(filters);
        const pagerOptions = <TEntity,>() => ({
          queryConstraints: constraints as QueryConstraint<
            keyof TEntity & string
          >[],
          orderBy: { field: "paidAt", direction: "desc" as const },
        });
        const [paypal, mercury, airwallex] = await Promise.all([
          fetchAllPages<PaypalPayment>(
            (options) => paypalPaymentRepository.getAll(options),
            pagerOptions<PaypalPayment>(),
          ),
          fetchAllPages<MercuryPayment>(
            (options) => mercuryPaymentRepository.getAll(options),
            pagerOptions<MercuryPayment>(),
          ),
          fetchAllPages<AirwallexPayment>(
            (options) => airwallexPaymentRepository.getAll(options),
            pagerOptions<AirwallexPayment>(),
          ),
        ]);
        return { paypal, mercury, airwallex };
      } finally {
        setExporting(false);
      }
    },
    [],
  );

  return { fetchAll, exporting };
}
